import { assignValue } from '../../../utils/stringUtil';
import { dogClubOptions } from '../components/EditCoverableAttribute/pet/DogClubChange';

import type { EditCoverablePriceDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import type {
    AttributeChangeInformationProps,
    AttributeChangeInformationMobileProps,
    AttributeChangeInformationDesktopProps
} from '../types';
import type {
    ACCContractType_Top,
    ACCFulltimeOrLeisure_Top,
    DogClub_Top,
    DSSPaymentFrequency,
    ProductName_Top,
    YesNo_Top
} from 'gw-edge-client/dist/types/dto/pc/typekey';
import { paymentFrequencyMap, getCurrencyString } from '../../../utils/currencyUtil';
import { type DiffDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { voluntaryAlarmOptions } from '../components/EditCoverableAttribute/privateContent/ContentVoluntaryAlarmChange';
import { getEditCoverableKey } from '../utils/editAttributeUtil';
import { fulltimeLeisureOptions } from '../components/EditCoverableAttribute/accident/AdultAccFulltimeLeisureChange';
import { contractTypeOptions } from '../components/EditCoverableAttribute/accident/AdultAccContractTypeChange';

const attributeLabelMap: Record<string, string> = {
    DogClub: 'Hundeklub',
    VoluntaryAlarm: 'Frivillig alarm',
    Employment: 'Beskæftigelse',
    FullTimeOrLeisure: 'Tidsrum',
    YearlyMileage: 'Kørselsbehov',
    OdometerReading: 'Kilometerforbrug',
    ContractType: 'Variant'
};

const getDisplayAttributeValue = (
    lob: DiffDTO,
    optionList?: Array<{
        label: string;
        value: DogClub_Top | YesNo_Top | ACCFulltimeOrLeisure_Top | ACCContractType_Top;
    }>
): { oldAttribute: string; newAttribute: string } => {
    if (!optionList)
        return {
            oldAttribute: getAttributeValue(lob),
            newAttribute: getAttributeValue(lob, true)
        };
    return {
        oldAttribute:
            optionList.find(
                (option) => option.value === assignValue(lob.before?.valueType ?? '', lob.before)
            )?.label ?? '',
        newAttribute:
            optionList.find(
                (option) => option.value === assignValue(lob.after?.valueType ?? '', lob.after)
            )?.label ?? ''
    };
};

const attributeValueMap = (lob: DiffDTO): { oldAttribute: string; newAttribute: string } => {
    switch (lob.attribute) {
        case 'DogClub':
            return getDisplayAttributeValue(lob, dogClubOptions);
        case 'VoluntaryAlarm':
            return getDisplayAttributeValue(lob, voluntaryAlarmOptions);
        case 'FullTimeOrLeisure':
            return getDisplayAttributeValue(lob, fulltimeLeisureOptions);
        case 'ContractType':
            return getDisplayAttributeValue(lob, contractTypeOptions);
        default:
            return getDisplayAttributeValue(lob);
    }
};

const getAttributeValue = (lob: DiffDTO, isNew: boolean = false): string => {
    const attributeDTO = isNew ? lob.after : lob.before;
    if (attributeDTO === undefined) return '-';
    return attributeDTO?.valueType === 'TypeKey'
        ? attributeDTO.typeKeyDisplayName ?? '-'
        : assignValue(attributeDTO?.valueType ?? '-', attributeDTO);
};

const getEditCoverableChangeProps = (
    editCoverablePrice: EditCoverablePriceDTO,
    productToChange: ProductName_Top,
    selectedPaymentFrequency?: DSSPaymentFrequency
): AttributeChangeInformationProps | undefined => {
    const coverableName = getEditCoverableKey(productToChange);
    const coverable = editCoverablePrice?.[coverableName]?.[0] ?? undefined;

    if (!coverable) return;

    const oldPriceLabel = 'DSS-COVERABLE-DETAILS-PRICE-AFFECT-BEFORE';
    const newPriceLabel = 'DSS-COVERABLE-DETAILS-PRICE-AFFECT-AFTER';

    const isMonthly = selectedPaymentFrequency === paymentFrequencyMap.monthly;

    const oldPriceAmount = isMonthly
        ? editCoverablePrice.oldPrice?.monthlyTotal?.amount
        : editCoverablePrice.oldPrice?.yearlyTotal?.amount;
    const newPriceAmount = isMonthly
        ? editCoverablePrice.newPrice?.monthlyTotal?.amount
        : editCoverablePrice.newPrice?.yearlyTotal?.amount;

    const oldPrice = getCurrencyString(oldPriceAmount ?? 0, ' kr.', 2);
    const newPrice =
        newPriceAmount !== undefined ? getCurrencyString(newPriceAmount ?? 0, ' kr.', 2) : '-';

    const attributeLabel = attributeLabelMap[coverable.attribute];
    const { oldAttribute, newAttribute } = attributeValueMap(coverable);

    const detailPriceText = 'DSS-OPC-PRICE-TABLE-SUBTITLE';
    const mobileProps: AttributeChangeInformationMobileProps = {
        before: {
            price: {
                label: oldPriceLabel,
                value: oldPrice
            },
            attribute: {
                label: attributeLabel,
                value: oldAttribute
            }
        },
        after: {
            price: {
                label: newPriceLabel,
                value: newPrice
            },
            attribute: {
                label: attributeLabel,
                value: newAttribute
            }
        }
    };
    const isOnetimePayment = selectedPaymentFrequency === paymentFrequencyMap.onetime;
    const oldPriceWithPaymentFrequency = `${isMonthly ? 'Månedlig' : 'Årlig'} pris: ${oldPrice}`;
    const newPriceWithPaymentFrequency = `${isMonthly ? 'Månedlig' : 'Årlig'} pris: ${newPrice}`;
    const desktopProps: AttributeChangeInformationDesktopProps = {
        before: {
            attributeValue: attributeLabel ? `${attributeLabel}: ${oldAttribute}` : oldAttribute,
            priceValue: isOnetimePayment ? oldPrice : oldPriceWithPaymentFrequency,
            priceLable: oldPriceLabel
        },
        after: {
            attributeValue: attributeLabel ? `${attributeLabel}: ${newAttribute}` : newAttribute,
            priceValue: isOnetimePayment ? newPrice : newPriceWithPaymentFrequency,
            priceLable: newPriceLabel
        }
    };
    return {
        mobileProps,
        desktopProps,
        detailPriceText
    };
};

export { getEditCoverableChangeProps };
